// HelperFunctions/GetARFirmName.js

const clean = (v) => (v ?? "").toString().trim();

/**  export this so other files can reuse it */
export const getPersonKey = (c) =>
  (c?.id || c?.email || c?.contactEmail || "").toString().trim().toLowerCase();

const normalizeStatuses = (raw) => {
  if (raw == null) return [];
  const parts = Array.isArray(raw) ? raw : [raw];

  return parts
    .flatMap((x) =>
      String(x)
        .split(/[,;|]/)
        .map((y) => y.trim())
    )
    .filter(Boolean)
    .map((s) => s.toUpperCase());
};

/**
 * Display name for an AR "firm":
 * - Prefer tradingName
 * - Otherwise use tradingStyles[] or legacy tradingStyle
 */
export const getArFirmName = (c) => {
  const tn = clean(c?.tradingName);
  if (tn) return tn;

  const arr = Array.isArray(c?.tradingStyles)
    ? c.tradingStyles.map(clean).filter(Boolean)
    : [];

  const single = clean(c?.tradingStyle);
  const styles = arr.length ? arr.join(" / ") : single;

  return clean(styles) || "Unknown";
};

/** Normalised firm key (safe for Sets / Maps) */
export const getArFirmKey = (c) => getArFirmName(c).toLowerCase().trim();

/** Count unique AR firms */
export const countUniqueArFirms = (records = []) => {
  const seen = new Set();
  for (const c of records) {
    const k = getArFirmKey(c);
    if (k) seen.add(k);
  }
  return seen.size;
};

/**
 * Build category counts where EACH AR FIRM counts once.
 * Statuses are aggregated across all advisers in the same firm.
 */
export const buildCountsByFirm = (records = [], cats = []) => {
  const counts = Object.fromEntries((cats || []).map((c) => [c, 0]));

  // firmKey -> Set(statuses)
  const firmStatusMap = new Map();

  for (const c of records) {
    const firmKey = getArFirmKey(c);
    if (!firmKey) continue;

    const statuses = normalizeStatuses(c.status ?? c.adviserStatus);

    if (!firmStatusMap.has(firmKey)) firmStatusMap.set(firmKey, new Set());
    const set = firmStatusMap.get(firmKey);
    statuses.forEach((s) => set.add(s));
  }

  for (const set of firmStatusMap.values()) {
    const hasFP = set.has("FP");
    const hasMA = set.has("MA");
    const hasGI =
      set.has("GI/PROT") || set.has("GI") || set.has("GI/PROTECTION");

    if (hasFP && hasMA) counts[cats[2]]++;
    else if (hasFP) counts[cats[0]]++;
    else if (hasMA) counts[cats[1]]++;
    else if (hasGI) counts[cats[3]]++;
    else counts[cats[4]]++;
  }

  return counts;
};

/**
 * Build category counts for Trading Styles where EACH ADVISER counts once.
 * (dedupe by id/email/contactEmail)
 */
export const buildCountsForTradingStyles = (records = [], cats = []) => {
  const counts = Object.fromEntries((cats || []).map((c) => [c, 0]));
  const seen = new Set();

  for (const c of records) {
    const key = getPersonKey(c);
    if (!key || seen.has(key)) continue;
    seen.add(key);

    const statuses = normalizeStatuses(c.status ?? c.adviserStatus);
    const hasFP = statuses.includes("FP");
    const hasMA = statuses.includes("MA");
    const hasGI =
      statuses.includes("GI/PROT") ||
      statuses.includes("GI") ||
      statuses.includes("GI/PROTECTION");

    if (hasFP && hasMA) counts[cats[2]]++;
    else if (hasFP) counts[cats[0]]++;
    else if (hasMA) counts[cats[1]]++;
    else if (hasGI) counts[cats[3]]++;
    else counts[cats[4]]++;
  }

  return counts;
};

/** Count unique Trading Styles advisers */
export const countUniqueTradingStyleAdvisers = (records = []) => {
  const seen = new Set();
  for (const c of records) {
    const k = getPersonKey(c);
    if (k) seen.add(k);
  }
  return seen.size;
};
